import { Link } from "react-router-dom";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { Reveal, Overline, FrameReveal } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import { useArticles } from "../lib/hooks";
import { formatArticleDate } from "../lib/site";

export default function Insights() {
  const { data: articles, isLoading } = useArticles();

  return (
    <div data-testid="insights-page">
      <Seo
        title="Insights | Building Advice from NOOR Design & Build"
        description="Practical, honest guidance on extensions, renovations, planning and the building process — from the team at NOOR Design & Build."
        path="/insights"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Insights", path: "/insights" }])}
      />
      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <Overline light>[ Insights ]</Overline>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 max-w-3xl leading-[1.05]">
            Straight answers for serious projects.
          </h1>
          <p className="text-white/60 text-base md:text-lg leading-relaxed mt-8 max-w-2xl">
            Practical guidance on planning, budgeting and running a building project — written plainly, without
            the sales patter.
          </p>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-28">
          {isLoading && <p className="text-graphite/50 text-sm">Loading articles…</p>}
          {!isLoading && (articles || []).length === 0 && (
            <p className="text-graphite/50 text-sm" data-testid="insights-empty">New articles are being prepared. Please check back shortly.</p>
          )}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* noindexed articles (e.g. consolidated Devon posts) are kept for direct links but not promoted here */}
            {(articles || []).filter((a) => !a.noindex).map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08}>
                <Link to={`/insights/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group block transition-transform duration-500 ease-out hover:-translate-y-1.5">
                  {a.cover && (
                    <div className="border border-black/10 p-2">
                      <FrameReveal src={a.cover} alt={a.title} className="aspect-[16/9]" />
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-4 mt-6">
                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-ember">[ {String(i + 1).padStart(2, "0")} ]</p>
                    <time data-testid={`article-date-${a.slug}`} dateTime={a.published_at || a.updated_at} className="font-mono text-[10px] tracking-[0.18em] uppercase text-graphite/40">
                      {formatArticleDate(a.published_at || a.updated_at)}
                    </time>
                  </div>
                  <h2 className="font-display font-light text-2xl md:text-3xl tracking-tight mt-3 group-hover:text-ember transition-colors duration-300">
                    {a.title}
                  </h2>
                  <p className="text-sm text-graphite/55 leading-relaxed mt-3">{a.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CtaBand />
    </div>
  );
}
